import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { School, Eye, EyeOff, Lock, Mail, Sparkles, ArrowRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../contexts/AuthContext'
import LanguageSelector from '../../components/LanguageSelector'

export default function Login() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { signIn } = useAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    const { data, error } = await signIn(email, password)

    if (error) {
      setError(error.message === 'Invalid login credentials' 
        ? t('auth.invalidCredentials')
        : error.message)
    } else {
      // Navigation will be handled by AuthContext/App based on role
    }

    setLoading(false)
  }

  return (
    <div className="min-h-screen bg-background flex relative overflow-hidden">
      {/* Background Effects - Light Theme */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-card-teal/50 via-card-lavender/30 to-card-blue/30" />
        <div className="absolute -top-40 -right-40 w-[600px] h-[600px] bg-primary-200/30 rounded-full blur-[150px]" />
        <div className="absolute -bottom-40 -left-40 w-[600px] h-[600px] bg-purple-200/30 rounded-full blur-[150px]" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-yellow-100/20 rounded-full blur-[200px]" />
      </div>

      {/* Grid Pattern */}
      <div className="absolute inset-0 opacity-[0.02]" style={{
        backgroundImage: `linear-gradient(rgba(15,23,36,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(15,23,36,0.3) 1px, transparent 1px)`,
        backgroundSize: '50px 50px'
      }} />

      {/* Left Side - Illustration */}
      <div className="hidden lg:flex lg:w-1/2 relative items-center justify-center p-12">
        <div className="relative max-w-lg">
          {/* Floating Cards */}
          <div className="card-elevated p-8 transform -rotate-3 hover:rotate-0 transition-transform duration-500">
            <div className="flex items-center mb-6">
              <div className="h-14 w-14 rounded-2xl gradient-primary flex items-center justify-center shadow-glow">
                <School className="h-7 w-7 text-white" />
              </div>
              <div className="ml-4">
                <h3 className="text-xl font-bold text-text-primary">Welcome Back!</h3>
                <p className="text-sm text-text-secondary">Sign in to continue</p>
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="card-green p-4 flex items-center rounded-xl">
                <div className="h-10 w-10 rounded-xl bg-success-100 flex items-center justify-center mr-4">
                  <Sparkles className="h-5 w-5 text-success-500" />
                </div>
                <div>
                  <p className="text-sm font-medium text-text-primary">Smart Learning</p>
                  <p className="text-xs text-text-secondary">AI-powered insights</p>
                </div>
              </div>
              
              <div className="card-orange p-4 flex items-center rounded-xl">
                <div className="h-10 w-10 rounded-xl bg-orange-100 flex items-center justify-center mr-4">
                  <Lock className="h-5 w-5 text-orange-500" />
                </div>
                <div>
                  <p className="text-sm font-medium text-text-primary">Secure Access</p>
                  <p className="text-xs text-text-secondary">Enterprise-grade security</p>
                </div>
              </div>
            </div>
          </div>

          {/* Floating Badge */}
          <div className="absolute -top-6 -right-6 card-elevated px-4 py-2 float">
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 rounded-full bg-success-500 animate-pulse" />
              <span className="text-sm font-medium text-text-primary">Live Platform</span>
            </div>
          </div>

          {/* Stats Badge */}
          <div className="absolute -bottom-6 -left-6 card-elevated px-4 py-3 float-delayed">
            <div className="flex items-center space-x-3">
              <div className="h-10 w-10 rounded-xl gradient-primary flex items-center justify-center">
                <span className="text-lg font-bold text-white">500+</span>
              </div>
              <div>
                <p className="text-sm font-medium text-text-primary">Schools</p>
                <p className="text-xs text-text-secondary">Trust us</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="flex-1 flex flex-col justify-center items-center p-4 sm:p-8 relative z-10">
        {/* Language Selector */}
        <div className="absolute top-6 right-6">
          <LanguageSelector size="small" />
        </div>

        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="h-20 w-20 rounded-2xl gradient-primary flex items-center justify-center mx-auto shadow-glow mb-6">
              <School className="h-10 w-10 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-text-primary mb-2 font-heading">
              {t('auth.signIn')}
            </h1>
            <p className="text-text-secondary">
              {t('common.appName')}
            </p>
          </div>

          {/* Login Card */}
          <div className="card-elevated p-8">
            {error && (
              <div className="mb-6 alert-error flex items-center">
                <svg className="h-5 w-5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                {error}
              </div>
            )}

            <form className="space-y-6" onSubmit={handleSubmit}>
              <div>
                <label htmlFor="email" className="form-label">
                  {t('auth.email')}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="input pl-12"
                    placeholder="you@school.com"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="password" className="form-label">
                  {t('auth.password')}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="input pl-12 pr-12"
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-text-primary transition-colors"
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <input
                    id="remember-me"
                    name="remember-me"
                    type="checkbox"
                    className="h-4 w-4 rounded border-gray-300 text-primary-500 focus:ring-primary-500"
                  />
                  <label htmlFor="remember-me" className="ml-2 block text-sm text-text-secondary">
                    {t('auth.rememberMe')}
                  </label>
                </div>

                <div className="text-sm">
                  <a href="#" className="font-medium text-primary-600 hover:text-primary-700 transition-colors">
                    {t('auth.forgotPassword')}
                  </a>
                </div>
              </div>

              <div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full btn-primary py-4"
                >
                  {loading ? (
                    <div className="flex items-center justify-center">
                      <div className="spinner h-5 w-5 mr-2" />
                      {t('common.loading')}
                    </div>
                  ) : (
                    <>
                      {t('auth.signIn')}
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </>
                  )}
                </button>
              </div>
            </form>

            <div className="mt-8">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-white text-text-secondary">{t('auth.newToApp')}</span>
                </div>
              </div>

              <div className="mt-6">
                <Link
                  to="/register-school"
                  className="w-full btn-secondary py-4"
                >
                  <Sparkles className="h-4 w-4 mr-2" />
                  {t('landing.registerSchool')}
                </Link>
              </div>
            </div>
          </div>

          {/* Footer */}
          <p className="mt-8 text-center text-sm text-text-secondary">
            © {new Date().getFullYear()} {t('common.appName')}. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  )
}
